import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AppContext } from 'context/appContext';
import {
  Outlet,
  useOutletContext,
  useParams,
  useFetcher,
  useNavigate,
  ActionFunction,
  useLoaderData,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import FormTabNavigation, {
  FormTab,
} from 'components/layout/FormTabNavigation';
import { ROUTES, routeWithParams } from 'router/routes';
import { Box } from '@mui/material';
import { PermitTransactionStatus } from 'types/Payments';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import {
  EntityTypes,
  Permit,
  PanelRootContext,
  Property,
  PropertyGroup,
  PermitType,
} from 'types';
import { getBlankPermit } from 'utils';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { TraxeroActionResponse } from 'types';
import {
  getPermitByID,
  createPermit,
  getEntityPermitTypes,
  updatePermit,
  refundPermit,
} from 'api';
import { logError } from 'utils';

export interface PermitPanelRouteParams {
  params: {
    tenantID: string;
    permitID: string;
    propertyID?: string;
    groupID?: string;
  };
}

export interface PermitDetailsLoaderData {
  _permit: Promise<Permit>;
  _permitTypes: Promise<PermitType[]>;
}

export interface PermitFormContext {
  permit: Permit;
  setPermit: (permit: Permit) => void;
  permitTypes: PermitType[];
  entity: Property | PropertyGroup;
  displayMode: string;
  fetcherKey: string;
}

export const permitFormLoader = async ({ params }: PermitPanelRouteParams) => {
  const { tenantID, permitID, propertyID, groupID } = params;

  if (permitID === '0') {
    const permitTypes = getEntityPermitTypes({
      tenantID: tenantID,
      entityType: propertyID ? EntityTypes.Property : EntityTypes.PropertyGroup,
      entityID: propertyID || groupID,
    });
    return { _permit: null, _permitTypes: permitTypes };
  } else {
    const permit = getPermitByID({
      tenantID: tenantID,
      permitID: permitID,
      entityType: propertyID ? EntityTypes.Property : EntityTypes.PropertyGroup,
      entityID: propertyID || groupID,
    });
    return { _permit: permit, _permitTypes: [] };
  }
};
export enum PermitFormActionHandler {
  REFUND = 'REFUND',
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  UNKNOWN = 'UNKNOWN',
}

export const permitFormAction: ActionFunction = async ({
  request,
}): Promise<TraxeroActionResponse<PermitFormActionHandler>> => {
  const {
    body,
    handler,
    permitTypeID,
    permitID,
    entityType,
    entityID,
    tenantID,
    paymentID,
    cancel,
  } = await request.json();

  let res: Response;
  try {
    switch (handler) {
      case PermitFormActionHandler.CREATE:
        res = await createPermit({
          tenantID,
          entityType,
          entityID,
          permitTypeID,
          body,
        });
        break;
      case PermitFormActionHandler.UPDATE:
        res = await updatePermit({
          tenantID,
          entityType,
          entityID,
          permitID,
          body,
        });
        break;
      case PermitFormActionHandler.REFUND:
        res = await refundPermit({
          tenantID,
          entityType,
          entityID,
          permitTypeID,
          permitID,
          paymentID,
          cancel,
          body,
        });
        break;
      default:
        res = new Response(null, {
          status: 500,
          statusText: 'unknown handler',
        });
    }
  } catch (error) {
    logError(error as Error);
    return {
      handler,
      status: 500,
      statusText: 'Internal Server Error',
    };
  }
  let message: string;
  try {
    const r = await res.json();
    message = r.message && r.message.length > 0 ? r.message : res.statusText;
  } catch (error) {
    message = res.statusText;
  }
  return {
    handler,
    status: res.status,
    statusText: message,
    message,
    body,
  };
};

const PermitFormLayout = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const { permitID } = useParams();
  const displayToast = useContext(ToastContext);
  const { entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup>>();
  const { _permit, _permitTypes } = useLoaderData() as PermitDetailsLoaderData;
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const [permit, setPermit] = useState<Permit>(getBlankPermit(state.tenant.id));
  const [permitTypes, setPermitTypes] = useState<PermitType[]>([]);
  const navigate = useNavigate();

  const getSuccessMessage = useCallback(
    (response: TraxeroActionResponse<PermitFormActionHandler>): string => {
      switch (response.handler) {
        case PermitFormActionHandler.CREATE:
          return t('create_success', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
          });
        case PermitFormActionHandler.REFUND:
          return t('refund_success', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
          });
        case PermitFormActionHandler.UPDATE:
          return t('update_success', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
          });
        default:
          return '';
      }
    },
    [t],
  );
  const getErrorMessage = useCallback(
    (response: TraxeroActionResponse<PermitFormActionHandler>) => {
      switch (response.handler) {
        case PermitFormActionHandler.CREATE:
          return t('create_error', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
            error: `${t('status')} ${response.status} - ${t('reason')}: ${
              response.statusText
            }`,
          });
        case PermitFormActionHandler.REFUND:
          return t('revoke_error', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
        case PermitFormActionHandler.UPDATE:
          return t('update_error', {
            ns: 'toasts',
            name: t('permit.label', { ns: 'entities' }),
            error: `${response.status}-${
              response.message || response.statusText
            }`,
          });
        default:
          return '';
      }
    },
    [t],
  );

  const disableRefunds: boolean = useMemo(() => {
    if (!state.entityPermissions.permit.update || !permit) {
      return true;
    }
    let _disableRefunds = true;
    const transactions = permit?.transactions;
    if (transactions) {
      const paidTransaction = transactions.find(
        (t) => t.status === PermitTransactionStatus.PAID,
      );
      if (paidTransaction) {
        // check if the transaction has a refund
        const refundTransaction = transactions.find(
          (t) =>
            t.status === PermitTransactionStatus.REFUNDED &&
            t.paymentID === paidTransaction.paymentID,
        );
        if (!refundTransaction) {
          _disableRefunds = false;
        }
      }
    }
    return _disableRefunds;
  }, [permit]);
  const tabs: FormTab[] = useMemo(
    () => [
      {
        label: `${t('tabs.details', { ns: 'permits' })}`,
        route: routeWithParams(ROUTES.PROPERTY_PERMIT_DETAILS, [
          state.tenant.id,
          entity.id || 0,
          permitID || 0,
        ]),
      },
      {
        label: `${t('tabs.refunds', { ns: 'permits' })}`,
        route: routeWithParams(ROUTES.PROPERTY_PERMIT_REFUNDS, [
          state.tenant.id,
          entity.id || 0,
          permitID || 0,
        ]),
        disabled: disableRefunds || permitID === '0',
      },
      {
        label: `${t('tabs.history', { ns: 'permits' })}`,
        route: routeWithParams(ROUTES.PROPERTY_PERMIT_HISTORY, [
          state.tenant.id,
          entity.id || 0,
          permitID || 0,
        ]),
        disabled: permitID === '0',
      },
    ],
    [state.tenant.id, entity.id, permitID],
  );
  const title = useMemo(() => {
    if (permit.id > 0) {
      return `${t('permit.label', { ns: 'entities' })} #${permit.permitNumber}`;
    } else {
      return '';
    }
  }, [permit, permitID]);

  // async loader handling
  useEffect(() => {
    const awaitData = async () => {
      const p = await _permit;
      if (!p) {
        const pt = await _permitTypes;
        setPermitTypes(pt);
        setPermit({
          ...permit,
          startTime: new Date().getTime(),
        });
      } else {
        setPermit(p);
        setPermitTypes([p.permitType]);
      }
    };
    awaitData();
  }, [_permit, _permitTypes]);

  // action response handlers
  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PermitFormActionHandler>;
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        if (response.handler !== PermitFormActionHandler.REFUND) {
          navigate(
            routeWithParams(ROUTES.PROPERTY_PERMITS, [
              state.tenant.id,
              entity.id,
            ]),
          );
        }
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher.data]);

  return (
    <Box
      flexGrow={1}
      display={'flex'}
      flexDirection={'column'}
      minHeight={0}
      sx={{
        overflow: 'hidden',
      }}
    >
      <FormHeaderBar />
      <FormTabNavigation tabs={tabs} title={title} hidden={permitID === '0'} />
      <Box
        flexGrow={1}
        minHeight={0}
        sx={{
          overflow: 'hidden',
        }}
      >
        <Outlet
          context={
            {
              entity,
              displayMode,
              fetcherKey,
              permit,
              setPermit,
              permitTypes,
            } satisfies PermitFormContext
          }
        />
      </Box>
    </Box>
  );
};

export default PermitFormLayout;
